import { ref, computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useFingerprint } from './useFingerprint';
import api from '@/services/api';

// Shared state across composable instances
const limitData = ref(null);
const loading = ref(false);
const error = ref(null);

// Constants
const GUEST_LIMIT = 3;
const FREE_USER_LIMIT = 5;

export function useGuestLimit() {
    const authStore = useAuthStore();
    const { getFingerprint } = useFingerprint();

    // Computed: Get the appropriate limit based on user type
    const LIMIT = computed(() => {
        if (authStore.hasTierAccess('TIER 1')) return 999; // Unlimited for premium
        if (authStore.isAuthenticated) return FREE_USER_LIMIT;
        return GUEST_LIMIT;
    });

    // Computed: Current count of questions answered today
    const answeredCount = computed(() => {
        if (authStore.hasTierAccess('TIER 1')) return 0;
        return limitData.value?.used ?? 0;
    });

    // Computed: Has reached the daily limit
    const hasReachedLimit = computed(() => {
        if (authStore.hasTierAccess('TIER 1')) return false;
        return answeredCount.value >= LIMIT.value;
    });

    // Computed: Questions remaining today
    const remainingQuestions = computed(() => {
        if (authStore.hasTierAccess('TIER 1')) return 999;
        return limitData.value?.remaining ?? LIMIT.value;
    });

    // Computed: Reset time
    const resetsAt = computed(() => limitData.value?.resets_at ?? null);

    // Computed: User type
    const userType = computed(() => {
        if (authStore.hasTierAccess('TIER 1')) return 'premium';
        if (authStore.isAuthenticated) return 'free';
        return 'guest';
    });

    /**
     * Fetch current limit status from API
     */
    async function fetchLimitStatus() {
        // Premium users don't need to check
        if (authStore.hasTierAccess('TIER 1')) {
            limitData.value = {
                can_submit: true,
                remaining: 999,
                limit: 999,
                used: 0,
                user_type: 'premium'
            };
            return limitData.value;
        }

        loading.value = true;
        error.value = null;

        try {
            const fingerprint = await getFingerprint();
            const response = await api.checkQuestionLimit({
                fingerprint: fingerprint,
                user_id: authStore.user?.id ?? null
            });

            limitData.value = response.data;
            return response.data;
        } catch (err) {
            console.error('Failed to fetch limit status:', err);
            error.value = err;

            // Fallback to conservative estimate
            if (!limitData.value) {
                limitData.value = {
                    can_submit: true,
                    remaining: LIMIT.value,
                    limit: LIMIT.value,
                    used: 0,
                    user_type: userType.value
                };
            }
            return limitData.value;
        } finally {
            loading.value = false;
        }
    }

    /**
     * Increment local counter after submission
     * (optimistic update - API will also track this)
     */
    function incrementCount() {
        if (limitData.value) {
            limitData.value = {
                ...limitData.value,
                used: (limitData.value.used || 0) + 1,
                remaining: Math.max(0, (limitData.value.remaining || 0) - 1),
                can_submit: (limitData.value.remaining || 0) - 1 > 0
            };
        }
    }

    /**
     * Update from API response (after submission)
     */
    function updateFromResponse(response) {
        if (response.questions_remaining !== undefined && response.questions_remaining !== null) {
            if (limitData.value) {
                limitData.value = {
                    ...limitData.value,
                    remaining: response.questions_remaining,
                    used: (limitData.value.limit || LIMIT.value) - response.questions_remaining,
                    can_submit: response.questions_remaining > 0
                };
            }
        }
    }

    /**
     * Reset local state (e.g., on logout)
     */
    function resetCount() {
        limitData.value = null;
    }

    /**
     * Check if user can submit (synchronous check based on cached data)
     */
    function canSubmit() {
        // Premium users always can submit
        if (authStore.hasTierAccess('TIER 1')) return true;

        // Check cached limit data
        if (limitData.value) {
            return limitData.value.can_submit ?? !hasReachedLimit.value;
        }

        // Default to allowing (will be checked on server)
        return true;
    }

    /**
     * Get fingerprint for submission (convenience method)
     */
    async function getFingerprintForSubmission() {
        return await getFingerprint();
    }

    return {
        // State
        answeredCount,
        hasReachedLimit,
        remainingQuestions,
        resetsAt,
        userType,
        loading,
        error,
        LIMIT,

        // Methods
        incrementCount,
        resetCount,
        canSubmit,
        fetchLimitStatus,
        updateFromResponse,
        getFingerprintForSubmission
    };
}
